/**
 * URL Metadata Service
 * Fetches title, description, and other metadata from URLs
 */
class UrlMetadataService {

  /**
   * Fetch metadata from a URL using a CORS proxy
   */
  async fetchMetadata(url) {
    try {
      console.log('🔍 Fetching metadata for URL:', url);

      // Validate URL first
      const urlObj = new URL(url);
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        throw new Error('Only HTTP and HTTPS URLs are supported');
      }

      // Use AllOrigins CORS proxy to fetch the page
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;

      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      console.log('✅ Successfully fetched HTML for:', url);

      // Extract metadata from HTML
      const metadata = this.parseMetadata(html, url);
      console.log('📋 Extracted metadata:', metadata);

      return metadata;

    } catch (error) {
      console.error('❌ Error fetching metadata for', url, ':', error);

      // Return default metadata based on URL
      return {
        title: this.generateDefaultTitle(url),
        description: '',
        image: null,
        siteName: this.extractDomain(url),
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Parse HTML content to extract metadata
   */
  parseMetadata(html, url) {
    try {
      // Create a temporary DOM parser
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract title (priority: og:title > twitter:title > title tag)
      const title =
        this.getMetaContent(doc, 'property', 'og:title') ||
        this.getMetaContent(doc, 'name', 'twitter:title') ||
        doc.querySelector('title')?.textContent ||
        this.generateDefaultTitle(url);

      // Extract description (priority: og:description > twitter:description > meta description)
      const description =
        this.getMetaContent(doc, 'property', 'og:description') ||
        this.getMetaContent(doc, 'name', 'twitter:description') ||
        this.getMetaContent(doc, 'name', 'description') ||
        '';

      // Extract image
      const image =
        this.getMetaContent(doc, 'property', 'og:image') ||
        this.getMetaContent(doc, 'name', 'twitter:image') ||
        null;

      // Extract site name
      const siteName =
        this.getMetaContent(doc, 'property', 'og:site_name') ||
        this.extractDomain(url);

      return {
        title: this.cleanText(title),
        description: this.cleanText(description),
        image,
        siteName: this.cleanText(siteName),
        success: true,
        error: null
      };

    } catch (error) {
      console.error('❌ Error parsing metadata:', error);
      return {
        title: this.generateDefaultTitle(url),
        description: '',
        image: null,
        siteName: this.extractDomain(url),
        success: false,
        error: 'Failed to parse page content'
      };
    }
  }

  /**
   * Helper: Get meta tag content
   */
  getMetaContent(doc, attribute, value) {
    const element = doc.querySelector(`meta[${attribute}="${value}"]`);
    return element ? element.getAttribute('content') : null;
  }

  /**
   * Helper: Clean text content
   */
  cleanText(text) {
    if (!text) return '';

    return text
      .trim()
      .replace(/\s+/g, ' ')
      .substring(0, 200) // Limit length
      .trim();
  }

  /**
   * Helper: Generate default title from URL
   */
  generateDefaultTitle(url) {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');
      const path = urlObj.pathname.split('/').filter(Boolean).join(' › ');

      if (path) {
        return `${domain} › ${path}`;
      } else {
        return domain;
      }
    } catch {
      return 'Untitled Link';
    }
  }

  /**
   * Helper: Extract domain from URL
   */
  extractDomain(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return 'Unknown Site';
    }
  }

  /**
   * Validate if URL is accessible
   */
  async validateUrl(url) {
    try {
      new URL(url);
      return { valid: true, error: null };
    } catch (error) {
      return { valid: false, error: 'Please enter a valid URL' };
    }
  }
}

export default new UrlMetadataService();